// GitHub backup via the Git Data REST API. No worker proxy needed —
// api.github.com is CORS-friendly for authenticated requests. Mirrors
// the Accelerando GitHubSyncer pattern but for a single-user browser
// app pushing the local Lightning-FS git tree.
//
// Strategy: snapshot the whole data/ tree on every push. Blob/tree/
// commit/ref dance, ~3+N HTTP calls where N = changed files (uploads
// dedupe server-side by content sha). For Playground's expected scale
// (KB-MB total, <500 files) this completes in seconds and stays well
// inside GitHub's 5000-req/hr authenticated budget.
//
// Ported from agicore-foundry/src/lib/github-sync.ts. Renamed file
// to avoid collision with Playground's existing github-sync.ts (which
// owns the per-channel sync feature — different concern; see
// docs/git-canonical-port-plan.md for the distinction).

import { walkDataTree } from "./git-store";

const API_BASE = "https://api.github.com";
const DEFAULT_BRANCH = "main";

export interface BackupAuth {
  /** Fine-grained PAT with contents:read+write on the target repo. */
  token: string;
  /** "<owner>/<repo>". The owner must be a user the PAT can write
   *  repos for. */
  repo: string;
}

export interface PushResult {
  /** New commit OID on the remote. */
  oid: string;
  /** https://github.com/<owner>/<repo>/commit/<oid> */
  url: string;
  /** File count in the snapshot. */
  fileCount: number;
}

class GithubError extends Error {
  status: number;
  body: string;
  constructor(message: string, status: number, body: string) {
    super(`${message} (HTTP ${status}): ${body.slice(0, 200)}`);
    this.name = "GithubError";
    this.status = status;
    this.body = body;
  }
}

async function api<T>(
  auth: BackupAuth,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${auth.token}`,
    Accept: "application/vnd.github+json",
    "X-GitHub-Api-Version": "2022-11-28",
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await resp.text();
  if (!resp.ok) {
    throw new GithubError(`${method} ${path} failed`, resp.status, text);
  }
  return (text ? JSON.parse(text) : ({} as T)) as T;
}

// ─── Repo provisioning ──────────────────────────────────────────────────────

/** Verify the repo exists and we can write to it. Auto-creates it as
 *  a private repo (with an initial commit so the default branch ref
 *  exists) when 404. The PAT must have repo-creation permission for
 *  auto-create to work; otherwise the caller should pre-create the
 *  repo manually and only pass through here. */
export async function ensureRepo(auth: BackupAuth): Promise<void> {
  const [owner, name] = auth.repo.split("/");
  if (!owner || !name) throw new Error(`invalid repo "${auth.repo}" — expected "<owner>/<name>"`);

  try {
    await api<unknown>(auth, "GET", `/repos/${owner}/${name}`);
    return;
  } catch (e) {
    if (!(e instanceof GithubError) || e.status !== 404) throw e;
  }

  // Repo missing — try to create. POST /user/repos creates under the
  // authenticated user; POST /orgs/<org>/repos creates under an org.
  // Try user first; if owner mismatches, fall through to org.
  const me = await api<{ login: string }>(auth, "GET", "/user");
  if (me.login.toLowerCase() === owner.toLowerCase()) {
    await api<unknown>(auth, "POST", "/user/repos", {
      name,
      private: true,
      auto_init: true,
      description: "BabyAI Playground backup — versioned local state.",
    });
  } else {
    await api<unknown>(auth, "POST", `/orgs/${owner}/repos`, {
      name,
      private: true,
      auto_init: true,
      description: "BabyAI Playground backup — versioned local state.",
    });
  }
}

// ─── Snapshot push ──────────────────────────────────────────────────────────

/** Upload the entire current data/ tree as a single commit. Idempotent
 *  by content — re-running with no local changes produces no new
 *  commit (the new tree sha matches the parent's, GitHub returns the
 *  same commit). */
export async function pushSnapshot(
  auth: BackupAuth,
  message: string,
): Promise<PushResult> {
  const [owner, name] = auth.repo.split("/");
  if (!owner || !name) throw new Error(`invalid repo "${auth.repo}"`);

  const files = await walkDataTree();
  if (files.length === 0) {
    throw new Error("nothing to back up — local data tree is empty");
  }

  // 1. Get parent commit sha + author identity.
  let parentSha: string | null = null;
  try {
    const ref = await api<{ object: { sha: string } }>(
      auth, "GET", `/repos/${owner}/${name}/git/refs/heads/${DEFAULT_BRANCH}`,
    );
    parentSha = ref.object.sha;
  } catch (e) {
    if (!(e instanceof GithubError) || e.status !== 404) throw e;
    // No ref yet — first push on an empty branch. parentSha stays null.
  }

  // 2. Create a blob per file. Sequential to keep within rate limits;
  //    bulk Tree API would let us inline base64 but staying with
  //    blobs-first keeps payloads small for any single file.
  const blobs: Array<{ path: string; sha: string }> = [];
  for (const f of files) {
    const blob = await api<{ sha: string }>(
      auth, "POST", `/repos/${owner}/${name}/git/blobs`,
      { content: f.content, encoding: "utf-8" },
    );
    blobs.push({ path: f.path, sha: blob.sha });
  }

  // 3. Compose the tree. base_tree omitted = full replacement of root
  //    contents. That's what we want — a snapshot, not a delta. Files
  //    absent from the new tree get removed.
  const tree = await api<{ sha: string }>(
    auth, "POST", `/repos/${owner}/${name}/git/trees`,
    {
      tree: blobs.map((b) => ({
        path: b.path,
        mode: "100644",
        type: "blob",
        sha: b.sha,
      })),
    },
  );

  // 4. Create the commit.
  const me = await api<{ login: string; email: string | null; name: string | null }>(
    auth, "GET", "/user",
  );
  const author = {
    name: me.name || me.login,
    email: me.email || `${me.login}@users.noreply.github.com`,
  };
  const commit = await api<{ sha: string }>(
    auth, "POST", `/repos/${owner}/${name}/git/commits`,
    {
      message,
      tree: tree.sha,
      parents: parentSha ? [parentSha] : [],
      author,
      committer: author,
    },
  );

  // 5. Move the ref. PATCH for existing ref, POST for new.
  if (parentSha) {
    await api<unknown>(
      auth, "PATCH", `/repos/${owner}/${name}/git/refs/heads/${DEFAULT_BRANCH}`,
      { sha: commit.sha, force: false },
    );
  } else {
    await api<unknown>(
      auth, "POST", `/repos/${owner}/${name}/git/refs`,
      { ref: `refs/heads/${DEFAULT_BRANCH}`, sha: commit.sha },
    );
  }

  return {
    oid: commit.sha,
    url: `https://github.com/${owner}/${name}/commit/${commit.sha}`,
    fileCount: files.length,
  };
}

/** Sanity-check the PAT by hitting /user. Returns the GitHub login on
 *  success. Used by the UI to validate input before persisting. */
export async function checkToken(token: string): Promise<string> {
  const me = await api<{ login: string }>({ token, repo: "" }, "GET", "/user");
  return me.login;
}

// ─── Restore (pull) ─────────────────────────────────────────────────────────

export interface RemoteFile {
  path: string;
  content: string;
}

export interface PullResult {
  /** Head commit OID we pulled from. */
  oid: string;
  files: RemoteFile[];
}

/** Snapshot the entire data/ subtree at the remote's main HEAD.
 *  Returns every file as text. Used by the restore-from-backup flow
 *  (Phase 4 — destructive, deserves its own confirm UX). */
export async function pullSnapshot(auth: BackupAuth): Promise<PullResult> {
  const [owner, name] = auth.repo.split("/");
  if (!owner || !name) throw new Error(`invalid repo "${auth.repo}"`);

  const ref = await api<{ object: { sha: string } }>(
    auth, "GET", `/repos/${owner}/${name}/git/refs/heads/${DEFAULT_BRANCH}`,
  );
  const headSha = ref.object.sha;

  const commit = await api<{ tree: { sha: string } }>(
    auth, "GET", `/repos/${owner}/${name}/git/commits/${headSha}`,
  );

  const tree = await api<{
    tree: Array<{ path: string; type: string; sha: string }>;
    truncated: boolean;
  }>(
    auth, "GET", `/repos/${owner}/${name}/git/trees/${commit.tree.sha}?recursive=1`,
  );
  if (tree.truncated) {
    throw new Error("remote tree too large — recursive listing was truncated. backup needs splitting.");
  }

  const dataBlobs = tree.tree.filter(
    (e) => e.type === "blob" && e.path.startsWith("data/"),
  );
  const files: RemoteFile[] = [];
  for (const entry of dataBlobs) {
    const blob = await api<{ content: string; encoding: string }>(
      auth, "GET", `/repos/${owner}/${name}/git/blobs/${entry.sha}`,
    );
    if (blob.encoding !== "base64") {
      throw new Error(`unexpected blob encoding "${blob.encoding}" for ${entry.path}`);
    }
    files.push({ path: entry.path, content: decodeBase64(blob.content) });
  }

  return { oid: headSha, files };
}

function decodeBase64(b64: string): string {
  // GitHub returns base64 with embedded newlines. atob handles the
  // characters; we still need a UTF-8 pass for non-ASCII content.
  const cleaned = b64.replace(/\n/g, "");
  const bin = atob(cleaned);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder("utf-8").decode(bytes);
}
